// ─── Transition types ────────────────────────────────────────────────────

// 'continuous' is deprecated — no longer selectable in the UI. Kept to avoid
// breaking existing project files that may contain this value.
export type TransitionType = 'cut' | 'dissolve' | 'fade_to_black' | 'fade_from_black' | 'continuous';

export interface TimelineTransition {
  id: number;
  fromShotId: number;
  toShotId: number;
  type: TransitionType;
  duration: number;   // seconds
  easing: string;
  videoObjectUrl?: string;   // Deprecated: was used for continuous transition video
  videoMimeType?: string;    // Deprecated: was used for continuous transition video
  videoHistoryId?: string;   // Deprecated: was used for continuous transition video
}

// ─── Audio types ─────────────────────────────────────────────────────────

export interface AudioClip {
  id: number;
  label: string;
  shotId: number;              // FK to owning shot (parent Initial shot for animatics)
  shotOffset: number;          // seconds from start of owning shot (replaces startTime)
  duration: number;
  volume: number;             // 0.0–1.0
  bufferOffset: number;       // seconds into the audio buffer to start playback (for trimming)
  audioBuffer: AudioBuffer | null;   // decoded (populated post-load)
  mimeType: string;
  lane: number;               // 0=dialog, 1=music, 2=SFX, 3=voiceover
  isMuted: boolean;
  fadeInTime: number;
  fadeOutTime: number;
  color: string;              // hex
  peaks: Float32Array | null; // pre-computed waveform
  audioFileUrl?: string;      // file:// URL from Unity serializer (embedded mode only)
  cdnUrl?: string;            // Vercel Blob CDN URL (TTS clips); Unity downloads natively
}

// ─── Video types ─────────────────────────────────────────────────────────

export interface VideoShot {
  shotId: number;
  historyId?: string;                  // FK → GenerationHistoryEntry.id
  objectUrl: string;
  mimeType: string;
}

// ─── Comment/Approval types ─────────────────────────────────────────────

export type ApprovalStatus = 'pending' | 'approved' | 'notes';

/** Normalize legacy 5-state approvals to the current 3-state model. */
export function normalizeApprovalStatus(status: string): ApprovalStatus {
  if (status === 'approved' || status === 'final') return 'approved';
  if (status === 'notes' || status === 'rejected' || status === 'needs_revision') return 'notes';
  return 'pending';
}

export interface Approval {
  id: number;
  shotId: number;
  status: ApprovalStatus;
  approvedBy: string;
  approvedAt: string;
  notes: string;
  revision: number;
}

export interface Comment {
  id: number;
  shotId: number | null;
  timecode: number | null;
  author: string;
  text: string;
  createdAt: string;
  resolvedAt: string | null;
  parentCommentId: number | null;
  annotationSvg?: string | null;
  screenplayPage?: number | null;   // page number for screenplay annotations (1-based)
  sceneId?: number | null;          // scene ID for screenplay page annotations
  userId?: string | null;           // Supabase user ID for ownership tracking
}

// ─── Document settings (numbering) ──────────────────────────────────────

export interface DocumentSettings {
  isNumberingLocked: boolean;
  separator: string;           // "", ".", or "-"
  useDigitsForShots: boolean;
  useFullAlphabet: boolean;    // true = A-Z, false = skip I,O,S (23-letter set)
  startingSceneNumber: number; // effective starting number (1 if not customized)
  sceneNumberingType: string;  // "NumberLetter" or "LetterNumber"
  shotNumberingType: string;   // "NumberLetter" or "LetterNumber"
}

// ─── Project types ───────────────────────────────────────────────────────

export interface TimelineProject {
  title: string;
  aspectRatio: string;
  sensorSize: string;
  /** Project frame rate in fps (24, 25, or 30). Sourced from documentsettings.frameratetype.
   *  Optional because review-session JSON persisted before this field was added won't carry it. */
  frameRate?: number;
  scenes: TimelineScene[];
  totalDuration: number;
  transitions: TimelineTransition[];
  audioClips: AudioClip[];
  videoShots: VideoShot[];
  comments: Comment[];
  approvals: Approval[];
  hasScreenplay: boolean;
  reviewSessionId?: string | null;
  readySceneIds?: number[];
  aiAudioHints?: Record<number, string>;  // sceneId → audio direction hint
  generationHistory?: import('./generationHistory.ts').GenerationHistoryEntry[];
  knownBlobHistoryIds?: string[];
  voiceAssignments?: import('./tts.ts').VoiceAssignment[];
  screenplayLocations?: ScreenplayLocation[];
  screenplayCharacters?: ScreenplayCharacter[];
  sketchOverlays?: Array<{ shotId: number; definition: string }>;
  documentSettings?: DocumentSettings;
  /** `.previs` schema version from `documentversion.fileversion`. Undefined when that table was
   *  unreadable — in practice the embedded slim DB, which omits it and is always Unity-migrated —
   *  so consumers must read undefined as "current schema", not "pre-migration file". */
  fileVersion?: number;
  lastModified?: string;
  baseline?: import('../previs/screenplayNormalizer.ts').BaselinePayload;
}

export interface TimelineScene {
  id: number;
  number: string;
  /** Raw stored scene number from the DB: '' = auto-numbered, non-empty = manual override.
   *  `number` above holds the computed *display* value; this preserves the override so structural
   *  edits (e.g. reorder) can recompute the display without clobbering custom numbers. Optional:
   *  scenes built outside the loader default to '' (treated as auto-numbered). */
  numberOverride?: string;
  /** The scene's OWN heading location text (`scenes.name`), canonical form — e.g. "BARN (OLD JOE MEMORY)".
   *  20260923 - no longer a copy of the linked row's name: on v52+ files `screenplayLocationId` alone
   *  decides which location the scene belongs to, and only a location RENAME (prefix swap) or the
   *  per-scene pencil changes this text — merge, delete and unlink never do. Every heading composer,
   *  export, prompt and fingerprint reads it as-is. */
  name: string;
  locationType: string;
  timeOfDay: string;
  color: string;
  shots: TimelineShot[];
  /** True when the scene is flagged omitted in Unity. Loaded (Slice B) so the Compact
   *  board can gray it, but filtered out of exports/playback/pickers by consumers and
   *  counted in numbering (Unity parity). Optional: scenes built outside the loader
   *  default to undefined (treated as not omitted). */
  isOmitted?: boolean;
  screenplayText?: string;
  screenplay?: ScreenplayData;
  /** v50 column (`scenes.rough_blocking`, renamed from `staging_cache`) — persisted JSON `{fingerprint,
   *  relations}` from a rough block, hydrated into the session staging cache so re-blocking reuses Unity's
   *  staging. Optional: scenes built outside the loader (or pre-v50 files) default to undefined. */
  roughBlocking?: string;
  /** v50 column (`scenes.suggested_shots`) — persisted `SuggestedShotsEnvelope` JSON (LLM/fallback shot
   *  suggestions + the input fingerprint). Optional: scenes built outside the loader default to undefined. */
  suggestedShots?: string;
  /** v41 column (`scenes.screenplay_location_id`), authoritative as of v52's backfill — FK into
   *  project.screenplayLocations. Optional: absent on pre-v41 files, FK-0 (no location) scenes,
   *  and scenes built outside the loader. */
  screenplayLocationId?: number;
  /** v52 column (`scenes.location_notes`) — per-scene location-flavored notes shown in the
   *  Locations tab. Optional: absent when empty or pre-v52. */
  locationNotes?: string;
}

export interface ShotCastMember {
  name: string;          // character name or generic label like "Female01"
  gender?: string;       // "Male", "Female", or undefined
  expression?: string;   // "Joy", "Neutral", etc. — omit if Neutral
}

/**
 * One (element → shot) coverage assignment from the v55 `screenplay_coverage_lines` join table —
 * the dataset the board panel's Coverage sub-view draws.
 *
 * `(elementType, elementId)` is the KEY, never `elementId` alone: the three screenplay element
 * tables have independent id spaces, so a bare id is unique only within a type (the same trap
 * PREVIS_SCHEMA.md documents for `original_element_id`). Transitions are structurally uncoverable
 * and never appear here.
 *
 * DISTINCT from `ScreenplayDialogue.shotId` / `ScreenplayAction.shotId`, which stay a scalar
 * "primary owning shot" — that column is the LEGACY full-screen lining view's store AND the
 * TTS/audio pipeline's element→shot join (ScratchPanel, AiTtsDialog), so it cannot be made
 * multi-valued. This table can: several shots may cover one element, each with its own line style.
 * The two datasets are seeded identically by the v55 backfill and diverge on later edits.
 */
export interface ShotCoverageLine {
  elementType: 'dialogue' | 'action';
  elementId: number;
  lineStyle: 'straight' | 'wavy';
  /** The element's `script_order` when the row was written. Render re-derives order from the LIVE
   *  element list (re-import renumbers script_order document-globally), so this is a tiebreaker /
   *  debugging aid, not an authority. */
  sortOrder: number;
}

export interface TimelineShot {
  id: number;
  sceneId: number;
  number: string;
  /** Raw stored shot number from the DB: '' = auto-lettered, non-empty = manual override. Twin of
   *  TimelineScene.numberOverride, and needed for the same reason: `number` above holds the computed
   *  *display* letter, so a reorder that recomputed from it would freeze whatever letter each shot
   *  happened to be showing. Optional: shots built outside the loader default to '' (auto-lettered). */
  numberOverride?: string;
  name: string;
  order: number;
  /** Playback duration in seconds. For an ANIMATIC (`isAnimatic`) shot this is a SUM: previsLoader
   *  folds the shot's Continuing keyframe durations into its Initial parent (minus the endpoint
   *  pose). It is therefore NOT the value of the `shots.duration` column — see `ownDuration`. */
  duration: number;
  /**
   * The shot's OWN `shots.duration` column, un-summed — set by previsLoader for animatic shots only
   * (for every other shot it equals `duration`, so the loader leaves it absent and readers fall back).
   *
   * 20260922 — this exists because `duration` above is a DERIVED number for animatics and anything
   * that writes the duration back writes that one column. The coverage overview's Dur cell did
   * exactly that: it seeded from the sum and Unity's ApplyCoverageToShot writes `duration=` with no
   * guard, so each Apply compounded the parent's own duration (1 + 3x2 showed 5, applied 5, reloaded
   * 9, applied 9, reloaded 13). Seed a round-tripping editor from THIS, never from `duration`.
   */
  ownDuration?: number;
  thumbnailDataUrl?: string;
  historyId?: string;                 // FK → GenerationHistoryEntry.id (applied AI generation)
  /** The RAW `shots.history_id` column ('' when empty), as loaded — never touched by
   *  resolveHistoryToShots' orphan-clear, which blanks `historyId` above when the FK resolves to no
   *  usable entry. sqliteBridge's refresh merge needs the two apart: a FK Unity actually cleared (a
   *  native import or a revert wrote '') must beat the in-memory value, while one the loader merely
   *  orphaned because its history ROW has not synced yet (metadata sync is debounced) must not.
   *  Only previsLoader sets it; shots built elsewhere leave it undefined. */
  rawHistoryId?: string;
  hasStaticImage?: boolean;           // true when shot has an imported/AI static image in the DB
  isPosterShot?: boolean;             // true when shots.ispostershot === 1 (the scene's poster shot)
  videoObjectUrl?: string;
  thumbHash?: string;                 // asset-freshness reconcile sweep (Task 14): Unity-side thumbnail content hash
  videoHash?: string;                 // asset-freshness reconcile sweep (Task 14): Unity-side video content hash
  notes?: string;
  focalLength: number;
  fStop: number;
  shotSize?: string;
  shotAngle?: string;
  shotMovement?: string;
  shotFraming?: string;               // e.g. 'SS', '2S OTS' — from shotframingtype DB column
  coverageColor?: string;             // hex color for coverage rail (e.g. '#4a90d9')
  /** v55 `screenplay_coverage_lines` rows owned by this shot, in `sort_order`. The PANEL Coverage
   *  sub-view's dataset — see ShotCoverageLine for why it is not `element.shotId`. Optional and
   *  absent (never `[]`) when the file predates v55 or the shot has no coverage. */
  coverageLines?: ShotCoverageLine[];
  isAnimatic?: boolean;
  cast?: ShotCastMember[];            // characters visible in this shot
  coverage?: string;                  // v50: coverage-suggestion JSON blob (written by applyCoverage)
  /**
   * The shot holds at least one labeled-crate stand-in — the blocking solver's placeholder for a
   * prop it could not resolve, drawn as a near-black box with a gold dashed edge and the prop's
   * name. Derived from `blockobjects` by previsLoader and NEVER saved: it is a fact about the 3D
   * scene, read back on every load, so there is no column behind it.
   *
   * Set ONLY when true (absent, not `false`, otherwise) — the flag exists so an Enhance render can
   * be told to replace the crates (promptBuilder's PLACEHOLDER_PROPS_CLAUSE), and every shot object
   * built outside the loader keeps comparing equal to a loaded one.
   */
  hasPlaceholderProps?: boolean;
}

// ─── Structured screenplay types (v41+) ─────────────────────────────

export interface ScreenplayCharacter {
  id: number;
  name: string;
  imageUrl?: string;  // character headshot/reference image (Vercel Blob URL from team upload)
  /** v50 column (`gendertype`) — used by the casting resolver's gender pool split. */
  gender?: string;
  /** v50 column (`appearance_desc`) — dossier text (heuristic parse or AI gap-fill). */
  appearance?: string;
  /** v50 column (`cast_bundled_id`) — persisted `BundledAvaturnRegistry` pick. */
  castBundledId?: string;
  /**
   * v53 column (`cast_placeholder`, INTEGER NOT NULL DEFAULT(0)) — provenance, not state:
   * `true` means this row was created by the import's deferred-pick branch and is genuinely
   * AWAITING a cast pick. Unity clears it the moment any real body lands.
   *
   * `undefined` on pre-v53 files (the loader's lower tiers), and that is deliberately NOT the same
   * as `false` for readers: an empty appearance/cast on a legacy row means "we cannot tell", which
   * is exactly the ambiguity this column exists to remove. Consumers must require `=== true`.
   */
  castPlaceholder?: boolean;
  /**
   * `characters.customcharacterid` — the cast row's link to a custom body. Emitted ONLY when the
   * row actually carries one (`> 0`), so `undefined` reads as "this cast member has no custom
   * body", the same thing the column's 0 default means.
   */
  customCharacterId?: number;
  /**
   * True when the linked custom body has a stored `customcharacters.avatar_recipe` — i.e. the
   * Avatar Customizer can re-open it for editing. False/absent means the body came from Tripo, a
   * file import or a legacy seed (or the slim DB carries no `customcharacters` table at all), and
   * the Script tab offers CUSTOM MODEL — the replace flow — instead of EDIT CHARACTER.
   */
  hasRecipe?: boolean;
}

export interface ScreenplayDialogue {
  id: number;
  sceneId: number;
  shotId: number;           // 0 = unassigned to a shot
  characterId: number;
  characterName: string;    // resolved from characters table
  dialogueText: string;
  parenthetical: string;    // empty string if none
  extension: string;        // 'V.O.', 'O.S.', 'O.C.', 'SUBTITLE', or ''
  isDualDialogue: boolean;
  scriptOrder: number;
}

export interface ScreenplayAction {
  id: number;
  sceneId: number;
  shotId: number;           // 0 = unassigned
  actionText: string;
  scriptOrder: number;
}

export interface ScreenplayTransitionElement {
  id: number;
  sceneId: number;
  transitionText: string;   // "CUT TO:", "DISSOLVE TO:", etc.
  scriptOrder: number;
}

export interface ScreenplayLocation {
  id: number;
  name: string;
  interiorExterior: string; // 'INT', 'EXT', 'I/E', or '' — non-authoritative hint (M3)
  /** v52: parsed/user-edited location description (Locations tab box). */
  description?: string;
  /** v52: machine-owned LLM location-blocking envelope JSON — opaque string here. */
  llmBlocking?: string;
  /** v52: location→set reference keys ("builtin:<SceneSetType>" / "custom:<file>", variation ordinal). */
  setKey?: string;
  setVariationKey?: string;
}

/** All structured screenplay elements for a scene */
export interface ScreenplayData {
  dialogue: ScreenplayDialogue[];
  actions: ScreenplayAction[];
  transitions: ScreenplayTransitionElement[];
}
